import React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  COUNTRY_LABELS, CountryCode, Organizer, countryOfBundesland, isEuForeign, normalizeVatId,
  vatIdMatchesCountry,
} from '@radevents/shared';
import { BORDER, FONT_MONO, INK, MUTED } from './organizerSpace';

/** Was die Felder halten — Empfänger, Anschrift, Land (ISO-Code) und bei EU-Kunden die USt-IdNr. */
export interface BillingValue {
  name: string;
  address: string;
  country: string;
  vatId: string;
}

interface Props {
  value: BillingValue;
  onChange: (next: BillingValue) => void;
  /** Enger Dialog (Hervorhebung) — dort stehen die Felder untereinander statt nebeneinander. */
  narrow?: boolean;
}

/*
 * Zwei Spalten: links Empfänger, Land und ggf. USt-IdNr. untereinander, rechts die Anschrift über
 * zwei Zeilen. Stand das Land allein in einer eigenen Zeile, blieb daneben ein Loch.
 * Unter 560px und im engen Dialog eine Spalte.
 *
 * Die Auswahlliste trägt keinen Browser-Stil (`appearance:none`): der native hat eigenes
 * Innenmaß und eigenen Pfeil und sitzt dann sichtbar anders als die Felder daneben. Der Pfeil
 * kommt aus lucide.
 */
export const BILLING_CSS = `
.bf { display:grid; gap:12px 14px; grid-template-columns:1fr 1fr; }
.bf-name, .bf-country, .bf-vat { grid-column:1; }
.bf-address { grid-column:2; grid-row:1 / span 2; display:flex; flex-direction:column; }
.bf-address textarea { flex:1; }
.bf--narrow { grid-template-columns:1fr; }
.bf--narrow > div { grid-column:1; grid-row:auto; }
@media (max-width:560px) {
  .bf { grid-template-columns:1fr; }
  .bf > div { grid-column:1; grid-row:auto; }
}
.bf label { display:block; font-family:${FONT_MONO}; font-size:10.5px; letter-spacing:.1em;
  text-transform:uppercase; color:${MUTED}; margin-bottom:6px; }
.bf input, .bf textarea, .bf select { width:100%; box-sizing:border-box; margin:0; font-family:inherit;
  font-size:14px; line-height:1.45; color:${INK}; background:#fff; border:1px solid ${BORDER};
  border-radius:10px; padding:10px 12px; }
.bf textarea { resize:vertical; min-height:74px; }
.bf select { appearance:none; -webkit-appearance:none; padding-right:36px; cursor:pointer; }
.bf-select { position:relative; }
.bf-select svg { position:absolute; right:12px; top:50%; transform:translateY(-50%);
  color:${MUTED}; pointer-events:none; }
.bf input:focus-visible, .bf textarea:focus-visible, .bf select:focus-visible { outline:2px solid #922C57; outline-offset:1px; }
.bf-error { font-size:12.5px; color:#8C3B0B; margin:6px 0 0; }
`;

/** Deutschland zuerst, danach alle übrigen Länder nach Namen. */
const COUNTRY_OPTIONS: { code: string; label: string }[] = [
  { code: 'DE', label: COUNTRY_LABELS.DE },
  ...(Object.keys(COUNTRY_LABELS) as CountryCode[])
    .filter(c => c !== 'DE')
    .map(c => ({ code: c, label: COUNTRY_LABELS[c] }))
    .sort((a, b) => a.label.localeCompare(b.label, 'de')),
];

/**
 * Passt die Angabe? Eine leere USt-IdNr. ist kein Fehler (dann eben mit MwSt.), eine, die nicht
 * zum Land passt, schon: der Server weist sie ab, und der Kunde soll das vor dem Klick sehen.
 */
export const billingVatIdError = (value: BillingValue): string | null =>
  isEuForeign(value.country) && normalizeVatId(value.vatId) !== '' && !vatIdMatchesCountry(value.country, value.vatId)
    ? `Die USt-IdNr. passt nicht zu ${COUNTRY_LABELS[value.country as CountryCode] ?? value.country}.`
    : null;

/** Sind die Pflichtangaben da und stimmig? */
export const billingComplete = (value: BillingValue): boolean =>
  value.name.trim() !== '' && value.address.trim() !== '' && billingVatIdError(value) === null;

/**
 * Rechnungsempfänger und -anschrift — Pflichtangaben jeder Buchung.
 *
 * **Warum schon bei der Bestellung und nicht erst nach der Zahlung:** der Verkauf läuft auf
 * Vorkasse (26.08.2026) — überwiesen wird mit einem Verwendungszweck, freigeschaltet nach
 * Geldeingang, die Rechnung folgt danach. Solange der Kunde etwas will, füllt er das Formular
 * aus; nach der Zahlung hinterherzutelefonieren wäre genau die Arbeit, die dieser Weg einspart.
 *
 * **Warum nicht einfach die Stammdaten nehmen:** die Anschrift in `organizers` stammt
 * größtenteils aus Importen und dem Adress-Enricher, ist also nicht vom Kunden bestätigt. Auf
 * eine Rechnung gehört sie nur, wenn er sie selbst so angegeben hat — deshalb ist sie hier
 * *vorgefüllt* und änderbar, nicht übernommen.
 *
 * Die Anschrift ist ein Textfeld und kein Satz aus Straße/PLZ/Ort: auf Vereinsrechnungen stehen
 * „z. Hd. Kassenwart", c/o-Zeilen und Postfächer, die kein Adressformular vorsieht.
 *
 * **Das Land ist ein eigenes Feld** (24.10.2026), weil es über die MwSt. entscheidet
 * (`taxTreatmentOf` in `shared/src/tax.ts`): außerhalb der EU und bei EU-Kunden mit USt-IdNr.
 * fällt keine an. Die USt-IdNr. wird nur bei EU-Ländern außerhalb Deutschlands gefragt.
 */
const BillingFields: React.FC<Props> = ({ value, onChange, narrow }) => {
  const { name, address, country, vatId } = value;
  const set = (patch: Partial<BillingValue>) => onChange({ ...value, ...patch });
  const vatError = billingVatIdError(value);
  return (
  <div className={narrow ? 'bf bf--narrow' : 'bf'}>
    <div className="bf-name">
      <label htmlFor="bf-name">Rechnung an</label>
      <input
        id="bf-name"
        value={name}
        onChange={e => set({ name: e.target.value })}
        placeholder="Vereins- oder Firmenname"
        autoComplete="organization"
        required
      />
    </div>
    <div className="bf-address">
      <label htmlFor="bf-address">Rechnungsanschrift</label>
      <textarea
        id="bf-address"
        value={address}
        onChange={e => set({ address: e.target.value })}
        placeholder={'Straße und Hausnummer\nPLZ Ort'}
        autoComplete="street-address"
        required
      />
    </div>
    <div className="bf-country">
      <label htmlFor="bf-country">Land</label>
      <div className="bf-select">
        <select
          id="bf-country"
          value={country}
          onChange={e => set({ country: e.target.value })}
          autoComplete="country"
        >
          {COUNTRY_OPTIONS.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
        <ChevronDown size={16} aria-hidden />
      </div>
    </div>
    {isEuForeign(country) && (
      <div className="bf-vat">
        <label htmlFor="bf-vatid">USt-IdNr. (falls vorhanden)</label>
        <input
          id="bf-vatid"
          value={vatId}
          onChange={e => set({ vatId: e.target.value })}
          placeholder="z. B. ATU12345678"
          aria-invalid={vatError ? true : undefined}
          aria-describedby={vatError ? 'bf-vatid-error' : undefined}
        />
        {vatError && <p className="bf-error" id="bf-vatid-error">{vatError}</p>}
      </div>
    )}
  </div>
  );
};

/**
 * Vorschlag aus den Stammdaten — nur eine Vorbelegung, siehe oben.
 *
 * Fehlt die Anschrift ganz (bei vielen Veranstaltern der Fall), bleibt das Feld leer statt
 * halb gefüllt: eine Zeile „45127" ohne Straße sieht aus, als sei sie geprüft worden.
 */
export const billingDefaults = (organizer: Organizer | null, fallbackName?: string): BillingValue => {
  const street = organizer?.street?.trim();
  const city = [organizer?.postalCode?.trim(), organizer?.city?.trim()].filter(Boolean).join(' ');
  return {
    name: organizer?.name || fallbackName || '',
    address: street && city ? `${street}\n${city}` : '',
    // Das Land aus der Region des Veranstalters: ein Verein aus dem Thurgau sieht dort die
    // Schweiz, ohne etwas zu wählen. Ohne Region gilt Deutschland.
    country: countryOfBundesland(organizer?.bundesland) ?? 'DE',
    vatId: '',
  };
};

/** Die Rechnungsangaben, wie sie an `CreateBookingRequest` gehen. */
export const billingRequest = (value: BillingValue) => ({
  billingName: value.name.trim(),
  billingAddress: value.address.trim(),
  billingCountry: value.country,
  vatId: isEuForeign(value.country) ? normalizeVatId(value.vatId) || undefined : undefined,
});

export default BillingFields;
